import { Box } from '@primer/react';
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from 'chart.js';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { useThongKeSiSo } from '../../hooks/useThongKeSiSo';
import { useCommonSelectedHook } from '../../hooks/useCommonSelectedHook';
import { useColors } from '../../hooks/useColors';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ChartSiSoTheoKhoi = () => {
    const { dataStructed, dmTrangThais, rawData } = useThongKeSiSo();
    const { nam_hoc } = useCommonSelectedHook();
    const { colors } = useColors();

    // Nhóm các trường theo dm_coso_id
    const cosoGroups = useMemo(() => {
        const cosoMap = new Map<number, {
            cosoId: number;
            cosoName: string;
            truongMap: Map<number, { truongId: number; truongName: string }>;
        }>();

        // Lấy danh sách trường và cơ sở từ rawData
        rawData.forEach((item: any) => {
            if (!cosoMap.has(item.dm_coso_id)) {
                cosoMap.set(item.dm_coso_id, {
                    cosoId: item.dm_coso_id,
                    cosoName: item.ten_co_so || `Cơ sở ${item.dm_coso_id}`,
                    truongMap: new Map()
                });
            }
            const cosoData = cosoMap.get(item.dm_coso_id)!;
            if (!cosoData.truongMap.has(item.dm_truong_id)) {
                cosoData.truongMap.set(item.dm_truong_id, {
                    truongId: item.dm_truong_id,
                    truongName: item.ten_truong
                });
            }
        });

        return Array.from(cosoMap.values());
    }, [rawData]);

    const chartsData = useMemo(() => {
        const hienHuuTrangThaiIds: number[] = [];
        dmTrangThais.forEach((value: any) => {
            const name = (value.dm_trangthaihocsinh_name || '').toLowerCase().trim();
            if (name === 'đang theo học' || name === 'đang theo học có điều kiện') {
                hienHuuTrangThaiIds.push(value.dm_trangthaihocsinh_id);
            }
        });

        return cosoGroups.map((cosoGroup) => {
            const result: { id: string; text: string; si_so: number; truongId: string }[] = [];
            const truongIds = Array.from(cosoGroup.truongMap.keys());

            dataStructed.forEach((value: any, truongIdStr: string) => {
                const truongId = parseInt(truongIdStr);
                if (truongIds.includes(truongId)) {
                    value.items.forEach((khoi: any) => {
                        let hienHuu = 0;
                        hienHuuTrangThaiIds.forEach(id => {
                            hienHuu += khoi.trangThaiSum.get(id) || 0;
                        });

                        result.push({
                            id: khoi.id,
                            text: khoi.name,
                            si_so: hienHuu,
                            truongId: truongIdStr
                        });
                    });
                }
            });

            // Tính tổng sĩ số cho mỗi trường
            const truongTotalMap = new Map<string, number>();
            result.forEach(item => {
                const current = truongTotalMap.get(item.truongId) || 0;
                truongTotalMap.set(item.truongId, current + item.si_so);
            });

            // Tạo legend cho cơ sở này
            const idNameMap = new Map<string, string>();
            cosoGroup.truongMap.forEach((truong) => {
                idNameMap.set(truong.truongId.toString(), truong.truongName);
            });

            const colorMap = new Map<string, string>();
            const items: { id: string; name: string; color: string; total: number }[] = [];
            let colorIndex = 0;
            idNameMap.forEach((name, id) => {
                const color = colors[colorIndex % colors.length].hex;
                colorMap.set(id, color);
                items.push({ id, name, color, total: truongTotalMap.get(id) || 0 });
                colorIndex++;
            });

            // Tính tổng sĩ số cho cơ sở
            const cosoTotal = result.reduce((sum, item) => sum + item.si_so, 0);

            return {
                cosoId: cosoGroup.cosoId,
                cosoName: cosoGroup.cosoName,
                cosoTotal,
                dataSource: result,
                legend: { colorMap, items }
            };
        });
    }, [dataStructed, dmTrangThais, cosoGroups, colors]);

    const chartsConfig = useMemo(() => {
        return chartsData.map((chartData) => {
            const labels = chartData.dataSource.map(item => item.text);
            const backgroundColors = chartData.dataSource.map(item => 
                chartData.legend.colorMap.get(item.truongId) as string
            );

            return {
                ...chartData,
                chartData: {
                    labels,
                    datasets: [
                        {
                            label: nam_hoc ? `Học sinh hiện hữu ${nam_hoc}` : 'Học sinh hiện hữu',
                            data: chartData.dataSource.map(item => item.si_so),
                            backgroundColor: backgroundColors,
                            borderColor: backgroundColors,
                            borderWidth: 1,
                            maxBarThickness: 32,
                            borderRadius: 2,
                        },
                    ],
                }
            };
        });
    }, [chartsData, nam_hoc]);

    const options: any = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 24
            }
        },
        plugins: {
            legend: {
                display: false
            },
            datalabels: {
                color: 'black',
                anchor: 'end',
                align: 'end',
                offset: 4,
                clamp: true,
                clip: false,
                font: {
                    weight: 'bold',
                    size: 13,
                },
            },
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    font: {
                        size: 11
                    }
                }
            },
            y: {
                beginAtZero: true,
                grace: '10%',
                grid: {
                    color: '#eeeeee'
                },
                ticks: {
                    font: {
                        size: 11
                    }
                }
            }
        }
    };

    return (
        <>
            <Box sx={{ fontSize: 24, fontWeight: 600 }}>
                {nam_hoc ? `Học sinh hiện hữu năm học ${nam_hoc}` : 'Học sinh hiện hữu theo khối'}
            </Box>
            <Box sx={{ fontSize: "1rem", color: "fg.muted" }}>
                Số học sinh hiện hữu theo từng khối
            </Box>
            <Box sx={{ mt: 3, display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 4 }}>
                {chartsConfig.map((chartConfig, idx) => (
                    <Box key={chartConfig.cosoId} sx={{ flex: chartsConfig.length > 1 ? "1 1 calc(50% - 16px)" : "1 1 100%", minWidth: "300px" }}>
                        {chartsConfig.length > 1 && (
                            <Box sx={{ fontSize: 18, fontWeight: 600, mb: 2, display: "flex", alignItems: "center" }}>
                                <Box>{chartConfig.cosoName}</Box>
                                <Box sx={{ ml: 1, color: "fg.muted", fontSize: 16 }}>({chartConfig.cosoTotal})</Box>
                            </Box>
                        )}
                        <Box sx={{ height: "280px" }}>
                            <Bar data={chartConfig.chartData} options={options} />
                        </Box>
                        <Box sx={{ mt: 2, display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 2 }}>
                            {chartConfig.legend.items.map((item) => (
                                <Box
                                    key={item.id}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        fontSize: 12
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: 0,
                                            backgroundColor: item.color,
                                            mr: 1
                                        }}
                                    />
                                    <Box sx={{ fontWeight: 500 }}>{item.name}</Box>
                                    <Box sx={{ ml: 1, color: "fg.muted" }}>({item.total})</Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Box>
        </>
    );
};

export default ChartSiSoTheoKhoi;